import React, { useRef, useEffect } from 'react';
import { Modal, Descriptions, Tag, Table, Divider } from 'antd';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const STATUS_MAP = {
  0: { text: '未处理', color: 'default' },
  1: { text: '已采纳', color: 'green' },
  2: { text: '待人工核查', color: 'orange' },
  3: { text: '人工拒绝', color: 'red' },
};

// 来源类型颜色
const SOURCE_COLORS = {
  API_VALIDATED: '#52c41a',
  SPORT_RECORD: '#1890ff',
  FIT_FILE: '#fa8c16',
  METASPACE_EVENT: '#722ed1',
};

const SOURCE_LABELS = {
  API_VALIDATED: 'API验证',
  SPORT_RECORD: '运动记录',
  FIT_FILE: 'monitor轨迹',
  METASPACE_EVENT: 'metaspace事件',
};

export default function BatchDetailModal({ visible, data, onClose }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    if (!visible || !data || !mapContainerRef.current) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(mapContainerRef.current, { center: [35, 105], zoom: 4 });
    L.tileLayer(
      'https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=7&x={x}&y={y}&z={z}',
      { attribution: '&copy; 高德地图', maxZoom: 19, subdomains: ['1', '2', '3', '4'] }
    ).addTo(map);
    mapRef.current = map;

    const bounds = [];
    const samples = data.samplePoints || [];

    samples.forEach((p) => {
      if (!p.longitude || !p.latitude) return;
      const latlng = [p.latitude, p.longitude];
      bounds.push(latlng);

      // 异常点用红色，正常点按来源类型着色
      const color = p.isOutlier ? '#f5222d' : (SOURCE_COLORS[p.sourceType] || '#aaa');
      const size = p.isOutlier ? 9 : 6;
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:${size}px;height:${size}px;background:${color};border:1px solid white;border-radius:50%;opacity:0.85"></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });

      L.marker(latlng, { icon })
        .addTo(map)
        .bindPopup(
          `<b>来源</b>：${SOURCE_LABELS[p.sourceType] || p.sourceType || '未知'}<br/>` +
          `精度：${p.horizontalAccuracy?.toFixed(0) ?? '-'}m<br/>` +
          `距中位数：${p.distanceToMedian?.toFixed(1) ?? '-'}m<br/>` +
          (p.isOutlier
            ? '<b style="color:#f5222d">异常点（已剔除）</b>'
            : '<span style="color:#52c41a">正常点</span>')
        );
    });

    // 初始中位数（蓝色大点）
    if (data.initialMedianLongitude && data.initialMedianLatitude) {
      const latlng = [data.initialMedianLatitude, data.initialMedianLongitude];
      L.marker(latlng, {
        icon: L.divIcon({
          className: 'custom-marker',
          html: '<div style="width:18px;height:18px;background:#1890ff;border:3px solid white;border-radius:50%;box-shadow:0 0 8px rgba(24,144,255,0.6)"></div>',
          iconSize: [18, 18], iconAnchor: [9, 9],
        }),
      }).addTo(map).bindPopup('<b>初始中位数</b>（所有样本点）').bringToFront();
      bounds.push(latlng);
    }

    // 最终中位数（绿色大点）
    if (data.finalMedianLongitude && data.finalMedianLatitude) {
      const latlng = [data.finalMedianLatitude, data.finalMedianLongitude];
      L.marker(latlng, {
        icon: L.divIcon({
          className: 'custom-marker',
          html: '<div style="width:18px;height:18px;background:#52c41a;border:3px solid white;border-radius:50%;box-shadow:0 0 8px rgba(82,196,26,0.6)"></div>',
          iconSize: [18, 18], iconAnchor: [9, 9],
        }),
      }).addTo(map).bindPopup('<b>最终中位数</b>（剔除异常点后）').bringToFront();
      bounds.push(latlng);
    }

    if (bounds.length > 0) {
      map.fitBounds(L.latLngBounds(bounds).pad(0.15));
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [visible, data]);

  const columns = [
    {
      title: '来源', dataIndex: 'sourceType', width: 110,
      render: (v) => SOURCE_LABELS[v] || v || '-',
    },
    { title: '用户ID', dataIndex: 'userId', width: 100 },
    {
      title: '经度', dataIndex: 'longitude', width: 120,
      render: (v) => v?.toFixed(7) ?? '-',
    },
    {
      title: '纬度', dataIndex: 'latitude', width: 120,
      render: (v) => v?.toFixed(7) ?? '-',
    },
    {
      title: '精度(m)', dataIndex: 'horizontalAccuracy', width: 80,
      render: (v) => v?.toFixed(0) ?? '-',
    },
    {
      title: '距中位数(m)', dataIndex: 'distanceToMedian', width: 110,
      render: (v) => v?.toFixed(1) ?? '-',
    },
    {
      title: '状态', dataIndex: 'isOutlier', width: 80,
      render: (v) => v
        ? <Tag color="red">异常</Tag>
        : <Tag color="green">正常</Tag>,
    },
  ];

  return (
    <Modal
      title={`批次详情 — ${data?.batchDate || ''}`}
      visible={visible}
      onCancel={onClose}
      width={1060}
      footer={null}
      destroyOnClose
    >
      {data && (
        <>
          {/* 计算摘要 */}
          <Descriptions column={3} size="small" bordered>
            <Descriptions.Item label="初始中位数">
              {data.initialMedianLongitude?.toFixed(7)}, {data.initialMedianLatitude?.toFixed(7)}
            </Descriptions.Item>
            <Descriptions.Item label="最终中位数">
              {data.finalMedianLongitude?.toFixed(7)}, {data.finalMedianLatitude?.toFixed(7)}
            </Descriptions.Item>
            <Descriptions.Item label="采信状态">
              <Tag color={STATUS_MAP[data.coordStatus]?.color}>
                {STATUS_MAP[data.coordStatus]?.text || '未知'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="MAD">{data.mad?.toFixed(1) ?? '-'}m</Descriptions.Item>
            <Descriptions.Item label="剔除阈值">
              {data.outlierThreshold?.toFixed(1) ?? '-'}m
            </Descriptions.Item>
            <Descriptions.Item label="偏差距离">
              {data.deviationDistance?.toFixed(1) ?? '-'}m
            </Descriptions.Item>
            <Descriptions.Item label="总样本数">{data.totalSampleCount ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="剔除异常点">
              <span style={{ color: data.outlierCount > 0 ? '#f5222d' : undefined, fontWeight: data.outlierCount > 0 ? 600 : undefined }}>
                {data.outlierCount ?? 0}
              </span>
            </Descriptions.Item>
          </Descriptions>

          {/* 样本分布地图 */}
          <Divider orientation="left" style={{ marginTop: 20 }}>样本分布</Divider>
          <div style={{ display: 'flex', gap: 14, marginBottom: 8, fontSize: 12, color: '#666' }}>
            <span><i style={dotStyle('#52c41a')} /> API验证</span>
            <span><i style={dotStyle('#1890ff')} /> 运动记录</span>
            <span><i style={dotStyle('#fa8c16')} /> monitor轨迹</span>
            <span><i style={dotStyle('#722ed1')} /> metaspace事件</span>
            <span><i style={dotStyle('#f5222d')} /> 异常点</span>
            <span><i style={dotStyle('#1890ff', 12)} /> 初始中位数</span>
            <span><i style={dotStyle('#52c41a', 12)} /> 最终中位数</span>
          </div>
          <div ref={mapContainerRef} style={{ width: '100%', height: 360, marginBottom: 16 }} />

          {/* 样本明细表格 */}
          <Divider orientation="left">样本明细</Divider>
          <Table
            dataSource={data.samplePoints}
            columns={columns}
            size="small"
            rowKey={(_, i) => i}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            scroll={{ y: 280 }}
          />
        </>
      )}
    </Modal>
  );
}

// 图例圆点样式
const dotStyle = (color, size = 10) => ({
  display: 'inline-block',
  width: size,
  height: size,
  borderRadius: '50%',
  background: color,
  marginRight: 4,
  verticalAlign: 'middle',
});
